export const versionMap = new Map<string, string>([
  ['acv', 'acv.xml'],
  ['asv', 'asv.xml'],
  ['bbe', 'bbe.xml'],
  ['bible.kjv', 'bible.akjv.xml'],
  ['bible.n.leb.2010', 'bible.en.leb.2010.xml'],
  ['bom', 'bom.xml'],
  ['bwe', 'bwe.xml'],
  ['darby', 'darby.xml'],
  ['diaglot', 'diaglot.xml'],
  ['dour', 'dour.xml'],
  ['emtv', 'emtv.xml'],
  ['etheridge', 'etheridge.xml'],
  ['gb', 'gb.xml'],
  ['isv', 'isv.xml'],
  ['jps', 'jps.xml'],
  ['jubl2000', 'jubl2000.xml'],
  ['kj2000', 'kj2000.xml'],
  ['kjv', 'kjv.xml'],
  ['leb', 'leb.xml'],
  ['leeser', 'leeser.xml'],
  ['lont', 'lont.xml'],
  ['lxxe', 'lxxe.xml'],
  ['mkjv1962', 'mkjv1962.xml'],
  ['mont', 'mont.xml'],
  ['nheb', 'nheb.xml'],
  ['nsb', 'nsb.xml'],
  ['oeb', 'oeb.xml'],
  ['rnt', 'rnt.xml'],
  ['roth', 'roth.xml'],
  ['rsv', 'rsv.xml'],
  ['rwebster', 'rwebster.xml'],
  ['rylt', 'rylt.xml'],
  ['tce', 'tce.xml'],
  ['tyndale', 'tyndale.xml'],
  ['ukjv', 'ukjv.xml'],
  ['updv', 'updv.xml'],
  ['wbs', 'wbs.xml'],
  ['web', 'web.xml'],
  ['wesley', 'wesley.xml'],
  ['wmth', 'wmth.xml'],
  ['wycliffe', 'wycliffe.xml'],
  ['xxx', 'xxx.xml'],
  ['ylt', 'ylt.xml']
]);

export const bookNames: Record<string, string> = {
  "Gen": "Genesis", "Exod": "Exodus", "Lev": "Leviticus", "Num": "Numbers",
  "Deut": "Deuteronomy", "Josh": "Joshua", "Judg": "Judges", "Ruth": "Ruth",
  "1Sam": "1 Samuel", "2Sam": "2 Samuel", "1Kgs": "1 Kings", "2Kgs": "2 Kings",
  "1Chr": "1 Chronicles", "2Chr": "2 Chronicles", "Ezra": "Ezra", "Neh": "Nehemiah",
  "Esth": "Esther", "Job": "Job", "Ps": "Psalms", "Prov": "Proverbs",
  "Eccl": "Ecclesiastes", "Song": "Song of Solomon", "Isa": "Isaiah", "Jer": "Jeremiah",
  "Lam": "Lamentations", "Ezek": "Ezekiel", "Dan": "Daniel", "Hos": "Hosea",
  "Joel": "Joel", "Amos": "Amos", "Obad": "Obadiah", "Jonah": "Jonah",
  "Mic": "Micah", "Nah": "Nahum", "Hab": "Habakkuk", "Zeph": "Zephaniah",
  "Hag": "Haggai", "Zech": "Zechariah", "Mal": "Malachi",
  "Matt": "Matthew", "Mark": "Mark", "Luke": "Luke", "John": "John",
  "Acts": "Acts", "Rom": "Romans", "1Cor": "1 Corinthians", "2Cor": "2 Corinthians",
  "Gal": "Galatians", "Eph": "Ephesians", "Phil": "Philippians", "Col": "Colossians",
  "1Thess": "1 Thessalonians", "2Thess": "2 Thessalonians",
  "1Tim": "1 Timothy", "2Tim": "2 Timothy", "Titus": "Titus", "Phlm": "Philemon",
  "Heb": "Hebrews", "Jas": "James", "1Pet": "1 Peter", "2Pet": "2 Peter",
  "1John": "1 John", "2John": "2 John", "3John": "3 John", "Jude": "Jude", "Rev": "Revelation",
  "Tob": "Tobit", "Jdt": "Judith", "AddEsth": "Additions to Esther",
  "Wis": "Wisdom of Solomon", "Sir": "Sirach", "Bar": "Baruch",
  "EpJer": "Letter of Jeremiah", "PrAzar": "Prayer of Azariah",
  "Sus": "Susanna", "Bel": "Bel and the Dragon",
  "1Macc": "1 Maccabees", "2Macc": "2 Maccabees",
  "1Esd": "1 Esdras", "2Esd": "2 Esdras",
  "3Macc": "3 Maccabees", "4Macc": "4 Maccabees",
  "Ps151": "Psalm 151", "Man": "Prayer of Manasseh",
  "AddDan": "Additions to Daniel"
};

export interface ChapterItem {
  type: 'chapter';
  osisID: string;
  label: string;
}

export interface VerseItem {
  type: 'verse';
  osisID: string;
  verseNum: string;
  html: string;
}

export type BookItem = ChapterItem | VerseItem;

export class RepositoryService {

  versionBookMap = new Map<string, string[]>()
  private _xmlCache = new Map<string, XMLDocument>()
  private _bookItemsCache = new Map<string, BookItem[]>()
  private _fetchPromises = new Map<string, Promise<XMLDocument>>()

  async getVersion(version: string): Promise<XMLDocument> {
    if (this._xmlCache.has(version)) return this._xmlCache.get(version)!;
    if (this._fetchPromises.has(version)) return this._fetchPromises.get(version)!;

    const filename = versionMap.get(version) ?? versionMap.get('acv')!;
    const promise = fetch(`/xml/${filename}`)
      .then(r => r.text())
      .then(text => {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(text, 'application/xml');
        this._xmlCache.set(version, xmlDoc);
        this._fetchPromises.delete(version);

        if (!this.versionBookMap.has(version)) {
          this.versionBookMap.set(version, [...xmlDoc.querySelectorAll('div[type=book]')]
            .map((i: Element) => i.getAttribute('osisID') || ''));
        }

        return xmlDoc;
      });

    this._fetchPromises.set(version, promise);
    return promise;
  }

  async getBookItems(version: string, book: string): Promise<BookItem[]> {
    const key = `${version}:${book}`;
    if (this._bookItemsCache.has(key)) return this._bookItemsCache.get(key)!;

    const doc = await this.getVersion(version);
    const bookEl = doc.querySelector(`div[type="book"][osisID="${book}"]`);
    if (!bookEl) return [];

    const items: BookItem[] = [];
    const chapters = bookEl.querySelectorAll('chapter');

    for (const chapter of chapters) {
      const chapterOsisID = chapter.getAttribute('osisID') || '';
      const chapterNum = chapterOsisID.split('.')[1] || '';
      items.push({ type: 'chapter', osisID: chapterOsisID, label: chapterNum });

      const verses = chapter.querySelectorAll('verse');
      for (const verse of verses) {
        const verseOsisID = verse.getAttribute('osisID') || '';
        const verseNum = verseOsisID.split('.')[2] || '';
        const html = verse.innerHTML
          .replaceAll('<note', '<bible-footnote')
          .replaceAll('</note', '</bible-footnote');
        items.push({ type: 'verse', osisID: verseOsisID, verseNum, html });
      }
    }

    this._bookItemsCache.set(key, items);
    return items;
  }

  getUnifiedBooksList(versions: string[]) {
    const ret = new Set<string>();
    for (const version of versions) {
      const books = this.versionBookMap.get(version);
      if (books) {
        for (const book of books) {
          ret.add(book);
        }
      }
    }
    return ret;
  }
}

export const repositoryService = new RepositoryService();
